import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { useSearch } from '../context/SearchContext';
import SearchSidebar from './SearchSidebar';
import { GetCheapestVariant, Price } from '@themes/common/components/ProductItem';
import clsx from 'clsx';
import PlaceholderImage from '@themes/common/components/PlaceholderImage';
import { GetCommonSettings } from '@themes/common/data';
import { StringReplaceCurlyBraces } from '@commons/replaceCurly';
const SearchResults = () => {
    const { translations } = GetCommonSettings();
    const { state, setSelectedIndex, selectItem } = useSearch();
    const { query, searchResults, isLoading, isOpen, selectedIndex, searchItems } = state;
    const [maxHeight, setMaxHeight] = useState(0);
    const resultsRef = useRef(null);
    const [focusedItemIndex, setFocusedItemIndex] = useState(-1);
    const [announcementText, setAnnouncementText] = useState('');
    const handleSubmit = (e) => {
        e.preventDefault();
        window.location.href = `/search?q=${encodeURIComponent((query === null || query === void 0 ? void 0 : query.trim()) || '')}`;
    };
    const hasSidebar = searchResults.collections.length > 0 || searchResults.articles.length > 0 || searchResults.pages.length > 0;
    const hasSearchQuery = (query === null || query === void 0 ? void 0 : query.trim()) && (query === null || query === void 0 ? void 0 : query.length) > 0;
    const totalResults = searchResults.products.length;
    // Handle keyboard navigation within search results
    const handleResultKeyPress = (e, index, product) => {
        var _a;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            window.location.href = `/products/${product.handle}`;
        }
        else if (e.key === 'ArrowDown') {
            e.preventDefault();
            // Find the next focusable element
            const nextIndex = Math.min(index + 1, searchResults.products.length - 1);
            const nextElement = document.getElementById(`search-result-${nextIndex}`);
            if (nextElement) {
                nextElement.focus();
                setFocusedItemIndex(nextIndex);
                setAnnouncementText(`${searchResults.products[nextIndex].title}, ${nextIndex + 1} of ${totalResults}`);
            }
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            // Find the previous focusable element
            const prevIndex = Math.max(index - 0, 0);
            const prevElement = document.getElementById(`search-result-${prevIndex}`);
            if (prevIndex === -1) {
                // Focus back on search input if at the top
                const searchInput = document.getElementById('site-search-input');
                if (searchInput) {
                    searchInput.focus();
                    setFocusedItemIndex(-1);
                    setAnnouncementText('Search input');
                }
            }
            else if (prevElement) {
                prevElement.focus();
                setFocusedItemIndex(prevIndex);
                setAnnouncementText(`${searchResults.products[prevIndex].title}, ${prevIndex + 1} of ${totalResults}`);
            }
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            // Close results and focus back on search input
            (_a = document.getElementById('site-search-input')) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else if (e.key === 'Tab' && !e.shiftKey && index === searchResults.products.length - 1) {
            // Handle focus at the end of results - wrap to see all button or first result
            const seeAllButton = document.querySelector('.absolute.bottom-0 button');
            if (seeAllButton && hasSearchQuery) {
                e.preventDefault();
                seeAllButton.focus();
            }
        }
        else if (e.key === 'Tab' && e.shiftKey && index === 0) {
            // Handle focus at beginning of results - focus back on search input
            e.preventDefault();
            const searchInput = document.getElementById('site-search-input');
            if (searchInput) {
                searchInput.focus();
            }
        }
    };
    // Handle keyboard navigation for the "See All Products" button
    const handleSeeAllKeyDown = (e) => {
        var _a, _b;
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            // Loop back to first result
            (_a = document.getElementById('search-result-0')) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            // Focus on last result
            (_b = document.getElementById(`search-result-${searchResults.products.length - 1}`)) === null || _b === void 0 ? void 0 : _b.focus();
        }
    };
    useEffect(() => {
        if (!isOpen)
            return;
        const calculateMaxHeight = () => {
            if (!resultsRef.current)
                return;
            const rect = resultsRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const spaceBelow = windowHeight - rect.top;
            const padding = 10; // Add some padding from the bottom of the viewport
            setMaxHeight(Math.max(0, spaceBelow - padding));
        };
        // Calculate initial height
        calculateMaxHeight();
        // Add scroll event listener
        window.addEventListener('scroll', calculateMaxHeight);
        window.addEventListener('resize', calculateMaxHeight);
        // Announce results when they first load
        if (!isLoading && searchResults.products.length > 0) {
            setAnnouncementText(`${searchResults.products.length} products found. Use arrow keys to navigate results.`);
        }
        else if (!isLoading && searchResults.products.length === 0 && query.trim()) {
            setAnnouncementText('No products found for your search.');
        }
        // Cleanup
        return () => {
            window.removeEventListener('scroll', calculateMaxHeight);
            window.removeEventListener('resize', calculateMaxHeight);
        };
    }, [isOpen, isLoading, searchResults.products.length, query]);
    // Reset focus state when results close
    useEffect(() => {
        if (!isOpen) {
            setFocusedItemIndex(-1);
        }
    }, [isOpen]);
    // Update focus when selectedIndex changes from keyboard navigation in search input
    useEffect(() => {
        var _a;
        if (selectedIndex >= 0 && ((_a = searchItems[selectedIndex]) === null || _a === void 0 ? void 0 : _a.type) === 'PRODUCT') {
            const productIndex = searchResults.products.findIndex((p) => p.id === searchItems[selectedIndex].item.id);
            if (productIndex >= 0) {
                setFocusedItemIndex(productIndex);
                // Try to focus the element
                setTimeout(() => {
                    var _a;
                    (_a = document.getElementById(`search-result-${productIndex}`)) === null || _a === void 0 ? void 0 : _a.focus();
                }, 10);
            }
        }
    }, [selectedIndex, searchItems, searchResults.products]);
    if (!isOpen) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx("div", Object.assign({ "aria-live": 'polite', className: 'sr-only', role: 'status' }, { children: announcementText })), _jsxs("div", Object.assign({ ref: resultsRef, id: 'search-results-list', role: 'listbox', "aria-label": translations.search.search_results || 'Search results', className: clsx('absolute z-10 w-full flex flex-col-reverse lg:flex-row gap-6 bg-white rounded-lg shadow-lg p-4 overflow-hidden text-base mt-1', {
                    'lg:flex-row': hasSidebar,
                }), style: { maxHeight: `${maxHeight}px` } }, { children: [_jsx(SearchSidebar, { collections: searchResults.collections, articles: searchResults.articles, pages: searchResults.pages }), _jsxs("div", Object.assign({ className: clsx('w-full lg:w-3/4 relative overflow-y-auto', {
                            'lg:w-full': !hasSidebar,
                            'mb-16': hasSearchQuery,
                        }), "aria-busy": isLoading }, { children: [_jsxs("h2", Object.assign({ className: 'text-sm! font-bold! normal-case! mb-3 text-primary! flex items-center gap-2' }, { children: [_jsx("div", { children: translations.search.products }), _jsx("div", { className: 'bg-primary flex-1 h-px' })] })), _jsxs("div", Object.assign({ className: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-2' }, { children: [searchResults.products.map((product, index) => {
                                        var _a, _b, _c, _d, _e, _f;
                                        const variant = GetCheapestVariant(product);
                                        const imageUrl = ((_a = product.featuredImage) === null || _a === void 0 ? void 0 : _a.url) || ((_d = (_c = (_b = product.images) === null || _b === void 0 ? void 0 : _b.nodes) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.url) || '';
                                        const { compare_at_price_display, price_display, unit, price, compareAtPrice } = Price(product);
                                        const isSelected = ((_e = searchItems[selectedIndex]) === null || _e === void 0 ? void 0 : _e.item.id) === product.id && ((_f = searchItems[selectedIndex]) === null || _f === void 0 ? void 0 : _f.type) === 'PRODUCT';
                                        const variantId = ((variant === null || variant === void 0 ? void 0 : variant.id) || '').split('/').pop() || '';
                                        const url = `/products/${product.handle}?variant=${variantId}`;
                                        return (_jsxs("a", Object.assign({ id: `search-result-${index}`, href: url, role: 'option', "aria-selected": isSelected, tabIndex: isSelected || focusedItemIndex === index ? 0 : -1, "aria-posinset": index + 1, "aria-setsize": searchResults.products.length, className: clsx('flex flex-row gap-4 p-2 hover:bg-gray-100! rounded-md overflow-hidden', {
                                                'bg-gray-100': isSelected,
                                                'outline-solid outline-2 outline-blue-500': focusedItemIndex === index,
                                            }), onMouseEnter: () => setSelectedIndex(-1), onKeyDown: (e) => handleResultKeyPress(e, index, product), onClick: () => selectItem({ type: 'PRODUCT', item: product }) }, { children: [_jsx("div", Object.assign({ className: 'w-24 h-24 shrink-0' }, { children: imageUrl ? (_jsx("img", { src: imageUrl, alt: '', className: 'w-full h-full object-contain', "aria-hidden": 'true' })) : (_jsx(PlaceholderImage, { "aria-hidden": 'true' })) })), _jsxs("div", Object.assign({ className: 'flex-1 flex flex-col' }, { children: [_jsx("h3", Object.assign({ className: 'text-base! font-medium! m-0 line-clamp-2 leading-5 capitalize! mb-auto text-black' }, { children: product.title.replace(/&nbsp;/g, ' ').replace('&nbsp;', ' ') })), _jsx("div", { className: 'sb-lipscore', "data-ls-product-name": product.title, "data-ls-brand": product.vendor, "data-ls-product-id": variant === null || variant === void 0 ? void 0 : variant.sku, "data-ls-product-url": `${global.location.origin}${url}` }), _jsxs("div", Object.assign({ className: 'price product-item__price h-auto! items-center', "aria-label": compareAtPrice > price
                                                                ? `Price: ${price_display} per ${unit}, reduced from ${compare_at_price_display}`
                                                                : `Price: ${price_display} per ${unit}` }, { children: [_jsxs("div", Object.assign({ className: clsx('price__current', {
                                                                        'price__current--on-sale': compareAtPrice > price,
                                                                    }) }, { children: [price_display, _jsxs("span", Object.assign({ className: 'sb-units' }, { children: [" / ", unit] }))] })), compareAtPrice > price && (_jsxs("div", Object.assign({ className: 'price__compare-at visible' }, { children: [compare_at_price_display, _jsxs("span", Object.assign({ className: 'sb-units' }, { children: [" / ", unit] }))] })))] }))] }))] }), product.id));
                                    }), searchResults.products.length === 0 && !isLoading && (_jsx("div", Object.assign({ className: 'col-span-2 py-8 text-center text-gray-500', role: 'status', "aria-live": 'polite' }, { children: StringReplaceCurlyBraces(query.length ? translations.search.no_results_title : translations.search.no_result_found, {
                                            query,
                                            term: query,
                                            terms: query,
                                        }) }))), isLoading && (_jsx("div", Object.assign({ className: 'col-span-2 py-8 text-center text-gray-500', role: 'status', "aria-live": 'polite' }, { children: translations.search.loading || 'Loading results...' })))] }))] })), searchResults.products.length > 0 && hasSearchQuery && (_jsx("div", Object.assign({ className: 'flex justify-center absolute bottom-0 w-full lg:w-3/4 p-3 bg-white right-0' }, { children: _jsx("button", Object.assign({ className: 'bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-6 rounded-md transition duration-200 cursor-pointer border-none', onClick: handleSubmit, onKeyDown: handleSeeAllKeyDown, "aria-label": translations.search.see_all_products || 'See all products matching your search' }, { children: translations.search.see_all_products })) })))] }))] }));
};
export default SearchResults;
